import type { ComponentType } from 'react'
import type { HubCardIconVariant } from '@/shared/ui/HubCard'
import {
  ArrowUpTrayIcon,
  BrainIcon,
  BookIcon,
  CalendarIcon,
  CalendarClockIcon,
  CertificateIcon,
  MessageSquareIcon,
  PackageIcon,
  RssIcon,
  SettingsIcon,
  SparklesIcon,
  UserFileIcon,
  UserIcon,
  UsersIcon,
} from '@/shared/ui/icons'

/** Categorias do hub Configurações (Opção A — mesma taxonomia da sidebar). */
export type SettingsCategoryId = 'conta' | 'cadastros' | 'clinica' | 'ajuda' | 'admin'

export interface SettingsCategoryDef {
  id: SettingsCategoryId
  /** Rótulo curto (submenu lateral / rail). */
  label: string
  /** Título da seção no hub. */
  sectionLabel: string
  hint: string
  adminOnly?: boolean
}

export const SETTINGS_CATEGORIES: SettingsCategoryDef[] = [
  { id: 'conta', label: 'Conta', sectionLabel: 'Conta', hint: 'Você' },
  {
    id: 'cadastros',
    label: 'Cadastros',
    sectionLabel: 'Cadastros & dados',
    hint: 'O que o app conhece',
  },
  {
    id: 'clinica',
    label: 'Clínica',
    sectionLabel: 'Clínica & operação',
    hint: 'Ferramentas do dia a dia',
  },
  {
    id: 'ajuda',
    label: 'Ajuda',
    sectionLabel: 'Ajuda & produto',
    hint: 'Novidades e feedback',
  },
  {
    id: 'admin',
    label: 'Admin',
    sectionLabel: 'Administração',
    hint: 'Plataforma',
    adminOnly: true,
  },
]

/** Configuração dos cards da página de Configurações. */
export interface SettingsCardConfig {
  to: string
  title: string
  subtitle: string
  category: SettingsCategoryId
  adminOnly?: boolean
  doctorOnly?: boolean
  iconVariant?: HubCardIconVariant
}

/** Item do submenu Configurações na sidebar (desktop) — atalhos de categoria. */
export interface SidebarMenuItem {
  to: string
  label: string
  icon: ComponentType<{ className?: string; size?: number }>
  /** Filtro de categoria; omitido = todas. */
  grupo?: SettingsCategoryId
  adminOnly?: boolean
  doctorOnly?: boolean
  state?: Record<string, unknown>
}

export const SETTINGS_CARD_ICONS: Record<
  string,
  ComponentType<{ className?: string; size?: number }>
> = {
  '/settings/profile': UserIcon,
  '/doctor/settings/certificate': CertificateIcon,
  '/doctor/settings/local-ai': BrainIcon,
  '/calendar': CalendarIcon,
  '/plantoes': CalendarClockIcon,
  '/inventory': PackageIcon,
  '/settings/cadastros': UserFileIcon,
  '/settings/import': ArrowUpTrayIcon,
  '/settings/feed': RssIcon,
  '/settings/admin-users': UsersIcon,
  '/settings/ai-providers': BrainIcon,
  '/settings/admin-platform/prompts': BrainIcon,
  '/feedback': MessageSquareIcon,
  '/documentation/novidades': SparklesIcon,
  '/settings/open-source': BookIcon,
}

/** Ordenação alfabética pt-BR por título ou rótulo. */
export const sortSettingsAlphabetically = <T extends { title?: string; label?: string }>(
  items: T[],
): T[] =>
  [...items].sort((a, b) =>
    (a.title ?? a.label ?? '').localeCompare(b.title ?? b.label ?? '', 'pt-BR', {
      sensitivity: 'base',
    }),
  )

const SETTINGS_CARDS_RAW: SettingsCardConfig[] = [
  {
    to: '/settings/profile',
    title: 'Perfil',
    subtitle: 'Foto, nome, CRM, especialidades e dados para impressão.',
    category: 'conta',
    iconVariant: 'neutral',
  },
  {
    to: '/settings/cadastros',
    title: 'Cadastros',
    subtitle:
      'Pacientes, documentos, tipos de exame, locais, tipos de atendimento, CID, princípios ativos e TUSS.',
    category: 'cadastros',
    doctorOnly: true,
    iconVariant: 'neutral',
  },
  {
    to: '/settings/import',
    title: 'Configurações avançadas',
    subtitle: 'Importação de dados via JSON, prompts do assistente e IA no dispositivo.',
    category: 'cadastros',
    iconVariant: 'neutral',
  },
  {
    to: '/calendar',
    title: 'Agenda',
    subtitle: 'Veja seus eventos, crie novos e sincronize com o Google Calendar.',
    category: 'clinica',
    iconVariant: 'neutral',
  },
  {
    to: '/plantoes',
    title: 'Plantões',
    subtitle: 'Horas trabalhadas, valores estimados e controle de turnos por local.',
    category: 'clinica',
    doctorOnly: true,
    iconVariant: 'neutral',
  },
  {
    to: '/inventory',
    title: 'Estoque',
    subtitle:
      'Medicamentos, vacinas e insumos — alertas de saldo baixo, opcional por paciente.',
    category: 'clinica',
    doctorOnly: true,
    iconVariant: 'neutral',
  },
  {
    to: '/doctor/settings/certificate',
    title: 'Assinatura digital',
    subtitle: 'Certificado, preferências ao salvar e período de autorização.',
    category: 'clinica',
    doctorOnly: true,
    iconVariant: 'neutral',
  },
  {
    to: '/settings/feed',
    title: 'Feed de notícias',
    subtitle: 'Notícias médicas da OMS, PubMed e outras fontes. Habilite ou desabilite feeds.',
    category: 'ajuda',
    iconVariant: 'neutral',
  },
  {
    to: '/documentation/novidades',
    title: 'O que há de novo',
    subtitle: 'Novidades e atualizações da documentação, mais recentes primeiro.',
    category: 'ajuda',
    iconVariant: 'neutral',
  },
  {
    to: '/feedback',
    title: 'Enviar feedback',
    subtitle: 'Sugestões, ideias e melhorias para o produto',
    category: 'ajuda',
    iconVariant: 'neutral',
  },
  {
    to: '/settings/open-source',
    title: 'Software livre e atribuições',
    subtitle: 'Licenças e créditos do Atlas Humano e das bibliotecas de código aberto.',
    category: 'ajuda',
    iconVariant: 'neutral',
  },
  {
    to: '/settings/admin-users',
    title: 'Usuários & Licença',
    subtitle: 'Gerencie usuários, planos e overrides de licenças.',
    category: 'admin',
    adminOnly: true,
    iconVariant: 'neutral',
  },
  {
    to: '/settings/ai-providers',
    title: 'Fornecedores de IA',
    subtitle: 'Catálogo de fornecedores, modelos padrão e chaves de plataforma.',
    category: 'admin',
    adminOnly: true,
    iconVariant: 'neutral',
  },
  {
    to: '/settings/admin-platform/prompts',
    title: 'Prompts da IA',
    subtitle: 'Gerencie os prompts de sistema usados pelas funcionalidades de IA da plataforma.',
    category: 'admin',
    adminOnly: true,
    iconVariant: 'neutral',
  },
]

const CATEGORY_ORDER = SETTINGS_CATEGORIES.map((c) => c.id)

/** Cards do hub, ordenados por categoria e depois alfabético dentro da seção. */
export const SETTINGS_CARDS: SettingsCardConfig[] = CATEGORY_ORDER.flatMap((catId) =>
  sortSettingsAlphabetically(SETTINGS_CARDS_RAW.filter((c) => c.category === catId)),
)

export const SETTINGS_PAGE_SUBTITLE =
  'Ajustes por área: conta, cadastros, clínica e ajuda. Use a busca ou Ctrl+K.'

export const SETTINGS_GRUPO_PARAM = 'grupo'

export const parseSettingsGrupo = (value: string | null): SettingsCategoryId | null => {
  if (!value) return null
  return SETTINGS_CATEGORIES.some((c) => c.id === value) ? (value as SettingsCategoryId) : null
}

export const settingsPathForGrupo = (grupo?: SettingsCategoryId | null): string => {
  if (!grupo) return '/settings'
  return `/settings?${SETTINGS_GRUPO_PARAM}=${grupo}`
}

/** Agrupa cards visíveis na ordem das categorias. */
export const groupSettingsCardsByCategory = (
  cards: SettingsCardConfig[],
): Array<{ category: SettingsCategoryDef; cards: SettingsCardConfig[] }> =>
  SETTINGS_CATEGORIES.map((category) => ({
    category,
    cards: cards.filter((c) => c.category === category.id),
  })).filter((section) => section.cards.length > 0)

const CATEGORY_SUBMENU_ICONS: Record<
  SettingsCategoryId,
  ComponentType<{ className?: string; size?: number }>
> = {
  conta: UserIcon,
  cadastros: UserFileIcon,
  clinica: CalendarIcon,
  ajuda: SparklesIcon,
  admin: UsersIcon,
}

/** Submenu lateral: atalhos de categoria (não lista cada card). */
export const SETTINGS_SUBMENU_ITEMS: SidebarMenuItem[] = [
  {
    to: '/settings',
    label: 'Todas',
    icon: SettingsIcon,
  },
  ...SETTINGS_CATEGORIES.map((cat) => ({
    to: settingsPathForGrupo(cat.id),
    label: cat.label,
    icon: CATEGORY_SUBMENU_ICONS[cat.id],
    grupo: cat.id,
    adminOnly: cat.adminOnly,
  })),
]

/** @deprecated Use SETTINGS_SUBMENU_ITEMS */
export const SIDEBAR_MENU_ITEMS = SETTINGS_SUBMENU_ITEMS

/** Rotas da área Configurações (expande o submenu; Agenda/Plantões/Estoque ficam fora). */
export const isSettingsSubmenuRoute = (pathname: string): boolean => {
  if (pathname === '/settings' || pathname.startsWith('/settings/')) return true
  if (pathname.startsWith('/doctor/settings/')) return true
  if (pathname === '/feedback') return true
  if (pathname === '/documentation/novidades') return true
  return false
}
